import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';

// Firebase Imports
import { logEvent } from 'firebase/analytics';
import { analytics, auth } from '../../firebase/firebase';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';

// Component Imports
import AuthModal from '../auth/AuthModal';
import ScrollCTA from '../ui/ScrollCTA';
// --- FIXED: Corrected relative import path ---
import LandingWalkthrough from '../features/LandingWalkthrough';

// Icon Imports
import {
  ChevronDown, ChevronLeft, ChevronRight,
  CheckCircle, FilePenLine, Lightbulb, Sparkles,
  BarChart3, Share2, FolderKanban, Rocket, Gem, PenTool,
  Menu, X, Star, Search, Gauge
} from 'lucide-react';

const googleProvider = new GoogleAuthProvider();

const LandingPage = () => {
  const navigate = useNavigate();

  // --- UI & DATA STATE ---
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [testimonialGroup, setTestimonialGroup] = useState(0);
  const testimonialIntervalRef = useRef(null);
  const carouselIntervalRef = useRef(null);

  // --- MODAL & CTA STATE ---
  const [showScrollCTA, setShowScrollCTA] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // --- AUTHENTICATION STATE ---
  const [authMode, setAuthMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tptStoreUrlInput, setTptStoreUrlInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  // --- Data Arrays ---
  const slides = [
    "https://imgur.com/1cy97jM.png", "https://i.imgur.com/wUP4gtb.png", "https://i.imgur.com/fqHWXIB.png", "https://imgur.com/peRNEdA.png", "https://i.imgur.com/E6fICcp.png",
    "https://i.imgur.com/2mS0Hsn.png", "https://imgur.com/Qnq6Buq.png", "https://i.imgur.com/TDS6mJX.png", "https://i.imgur.com/AXRjuxq.png",
    "https://i.imgur.com/8MjFkVM.png", "https://imgur.com/xnakn4y.png", "https://i.imgur.com/O5m7yne.png", "https://imgur.com/ullIjvM.png", "https://i.imgur.com/XIXxeGt.png",
    "https://i.imgur.com/xiUzlEA.png", "https://i.imgur.com/iQZr6PR.png", "https://imgur.com/ullIjvM.png"
  ];
  const testimonials = [
    { quote: "This tool makes writing my listings so much easier! Such a time saver!", author: "Bliss Valley Designs" },
    { quote: "My favorite is the SEO & Rank Tracker, but the Title Suggestion and Description Generator is a close 2nd. You can't beat the depth of this tool!", author: "World History Wisdom" },
    { quote: "The suggested keywords are excellent for helping me focus on important features of my product in the description.", author: "Literacy Cookbook" },
    { quote: "Just in one day I went from 10 views now I have 100+ views on the second day of using it. They are fast at replying and helping you out.", author: "Adapt and Grow with Ashley" },
    { quote: "This program is pretty amazing. The rank tracker is very helpful and gives thorough insights. It's cool to be able to see what is top ranked.", author: "Art is Basic" },
    { quote: "An incredibly helpful tool for any TPT seller serious about SEO and writing a good description for their products.", author: "Oceanview Resources" },
    { quote: "This application is fantastic. It is very intuitive to use, provides conversational answers, and is easy to connect to my shop.", author: "Happy & Bright Musicrooms" },
    { quote: "I can't believe how easy it is to use. There are many SEO tools out there, but this beats them all. This is a must for any TPT seller.", author: "Let's Flammingle" },
    { quote: "It helped immediately with views and helped me to maximize my listings. Amazing product and superb customer service!", author: "Elevate Your Success" }
  ];
  const navLinks = [
    { href: '/#features', label: 'Features' },
    { href: '/#how-to-use', label: 'How It Works' },
    { href: '/#pricing', label: 'Pricing' },
    { href: '/how-to-use', label: 'User Guide' },
    { href: '/blog', label: 'Blog' },
    { href: '/#faq', label: 'FAQ' },
  ];

  // --- AUTHENTICATION LOGIC ---
  const handleAuthAction = async (mode) => {
    setAuthError('');
    try {
      if (mode === 'signup') {
        await createUserWithEmailAndPassword(auth, email, password);
        logEvent(analytics, 'sign_up', { method: 'Email' });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        logEvent(analytics, 'login', { method: 'Email' });
      }
      setIsAuthModalOpen(false);
      navigate('/app');
    } catch (error) {
      setAuthError(error.message.replace('Firebase: ', ''));
    }
  };

  const handleGoogleSignIn = async () => {
    setAuthError('');
    try {
      await signInWithPopup(auth, googleProvider);
      logEvent(analytics, 'sign_up', { method: 'Google' });
      setIsAuthModalOpen(false);
      navigate('/app');
    } catch (error) {
      setAuthError(error.message.replace('Firebase: ', ''));
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setAuthError('');
    setResetMessage('');
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage("Password reset email sent! Please check your inbox.");
    } catch (error) {
      setAuthError(error.message.replace('Firebase: ', ''));
    }
  };

  // --- UI EFFECTS & HANDLERS ---
  useEffect(() => {
    carouselIntervalRef.current = setInterval(() => setCurrentSlide(prev => (prev + 1) % slides.length), 5000);
    testimonialIntervalRef.current = setInterval(() => setTestimonialGroup(prev => (prev + 3) % testimonials.length), 10000);
    return () => {
      clearInterval(carouselIntervalRef.current);
      clearInterval(testimonialIntervalRef.current);
    };
  }, [slides.length, testimonials.length]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 800 && !isAuthModalOpen) {
        setShowScrollCTA(true);
        window.removeEventListener('scroll', handleScroll);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAuthModalOpen]);

  const handleCarouselNavigation = (direction) => {
    clearInterval(carouselIntervalRef.current);
    setCurrentSlide(prev => {
      if (direction === 'next') return (prev + 1) % slides.length;
      return (prev - 1 + slides.length) % slides.length;
    });
    carouselIntervalRef.current = setInterval(() => setCurrentSlide(prev => (prev + 1) % slides.length), 5000);
  };

  const openAuthModal = (mode = 'signup') => {
    setAuthError('');
    setResetMessage('');
    setShowForgotPassword(false);
    setAuthMode(mode);
    setIsAuthModalOpen(true);
    setShowScrollCTA(false);
  };

  const downloadPdf = () => {
    logEvent(analytics, 'print_to_pdf_click', { source: 'Header_DownloadPDF' });
    window.print();
  };

  return (
    <>
      <Helmet>
        <title>Mr. Joel’s TPT Tools | All-in-One TPT Seller App</title>
        <meta
          name="description"
          content="Everything you need to optimize your TPT listings, track SEO performance, and generate marketing content — all in one powerful app made for sellers to help boost TPT store rankings & sales."
        />
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:title" content="Mr. Joel’s TPT Tools" />
        <meta
          property="og:description"
          content="All-in-one SEO and marketing platform built for TPT sellers. Generate & enhance TPT product SEO descriptions, rewrite titles, track rankings, and create social media content in one place."
        />
        <meta property="og:image" content="https://mrjoelstptgenerator.com/og-image.jpg" />
        <meta property="og:url" content="https://mrjoelstptgenerator.com" />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mr. Joel’s TPT Tools" />
        <meta
          name="twitter:description"
          content="Optimize your listings, track SEO, and market your store with one all-in-one app made for TPT sellers."
        />
        <meta name="twitter:image" content="https://mrjoelstptgenerator.com/og-image.jpg" />

        {/* PRINT STYLESHEET for high-quality PDF */}
        <style>{`
          @page { size: A4; margin: 0; }
          @media print {
            html, body { height: auto !important; overflow: visible !important; }
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              background: #fff !important;
            }
            header, nav, .print-hide, .chat-widget, .cookie-banner,
            .testimonial-grid + *, .scroll-cta, .download-pdf-btn, .carousel-controls, .carousel-dots, footer {
              display: none !important;
            }
            .print-header { display: block !important; padding: 16mm 18mm 6mm 18mm; border-bottom: 1px solid #e5e7eb; }
            * { animation: none !important; transition: none !important; }
            .print-container { max-width: 190mm !important; margin: 0 auto !important; padding: 0 0 12mm 0 !important; }
            .rounded-xl, .rounded-2xl { border-radius: 6px !important; }
            .shadow, .shadow-lg, .shadow-xl, .shadow-2xl, .shadow-md { box-shadow: none !important; }
            section, .hero-block { page-break-inside: avoid; padding: 12mm 0 !important; }
            #features, #how-to-use, #pricing, #faq { page-break-before: always; }
            .carousel-frame { position: static !important; height: auto !important; }
            .carousel-frame > div { display: none !important; position: static !important; opacity: 1 !important; }
            .carousel-frame > div:first-child { display: block !important; }
            a, button { box-shadow: none !important; background: transparent !important; color: #0c4a6e !important; border: 1px solid #e5e7eb !important; }
            a[href]:after { content: "" !important; }
            p { orphans: 3; widows: 3; }
          }
        `}</style>
      </Helmet>

      {/* Print-only header */}
      <div className="hidden print:block print-header">
        <div className="print-container">
          <h1 className="text-2xl font-extrabold" style={{color:'#0c4a6e'}}>Mr. Joel’s TPT Tools — All-in-One TPT Seller App</h1>
          <p className="text-sm" style={{color:'#334155'}}>mrjoelstptgenerator.com</p>
        </div>
      </div>

      <div className="bg-slate-50 text-slate-900 scroll-smooth text-[15.5px] md:text-base leading-relaxed">
        <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex-shrink-0">
                <a
                  href="/"
                  className="text-xl font-bold text-sky-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50 rounded"
                >
                  Mr Joel's Seller Tools
                </a>
              </div>
              <nav className="hidden md:flex items-center space-x-2">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="font-medium text-slate-600 hover:text-sky-600 transition-colors px-3 py-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
                  >
                    {link.label}
                  </a>
                ))}

                {/* HEADER PDF BUTTON REMOVED AS REQUESTED */}

                <button
                  onClick={() => {
                    logEvent(analytics, 'click_signup_trial', { source: 'Header_SignUp' });
                    openAuthModal('signup');
                  }}
                  className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded-full text-sm shadow-md hover:shadow-lg hover:scale-[1.02] transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
                >
                  Sign Up Free
                </button>
                <a
                  href="/app"
                  onClick={() => logEvent(analytics, 'login', { method: 'Direct', source: 'Header_Login' })}
                  className="text-slate-600 hover:text-sky-600 font-medium py-2 px-4 rounded-full text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
                >
                  Login
                </a>
              </nav>
              <div className="md:hidden flex items-center">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="inline-flex items-center justify-center p-2 rounded-md text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
                  aria-expanded={isMenuOpen}
                >
                  {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
          </div>
          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="text-slate-600 hover:bg-sky-50 hover:text-sky-700 block px-3 py-2 rounded-md text-base font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
                  >
                    {link.label}
                  </a>
                ))}
                {/* Keep mobile PDF option */}
                <button
                  onClick={() => { downloadPdf(); setIsMenuOpen(false); }}
                  className="w-full bg-white border border-slate-300 text-slate-700 block px-3 py-3 rounded-md text-base font-bold text-center mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
                >
                  Download PDF
                </button>
                <button
                  onClick={() => { openAuthModal('signup'); setIsMenuOpen(false); }}
                  className="w-full bg-sky-100 text-sky-700 block px-3 py-3 rounded-md text-base font-bold text-center mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
                >
                  Sign Up for Free
                </button>
                <a
                  href="/app"
                  className="w-full text-center block py-2 text-slate-600 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50 rounded"
                >
                  Login
                </a>
              </div>
            </div>
          )}
        </header>

        <main>
          <div className="bg-gradient-to-br from-sky-50 to-blue-50 py-16 sm:py-24 hero-block">
            <div className="max-w-5xl mx-auto px-4 flex flex-col items-center gap-10 text-center">

              <div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-sky-900 tracking-tight mb-2">
                  The All-in-One Toolkit for TPT Sellers
                </h1>
                <p className="mt-4 max-w-2xl mx-auto text-lg sm:text-xl text-slate-700">
                  Stop guessing and start growing. Our powerful tools help you write better listings, rank higher, and automate your marketing.
                </p>
              </div>

              {/* HERO BULLETS */}
              <div className="max-w-3xl w-full">
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-left">
                  <li className="flex items-center space-x-3"><CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" /> <span className="text-slate-700">Instantly write SEO-optimized descriptions.</span></li>
                  <li className="flex items-center space-x-3"><CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" /> <span className="text-slate-700">Track your product's live TPT rank.</span></li>
                  <li className="flex items-center space-x-3"><CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" /> <span className="text-slate-700">Analyze top-ranking competitors.</span></li>
                  <li className="flex items-center space-x-3"><CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" /> <span className="text-slate-700">Generate a week's worth of social media.</span></li>
                  <li className="flex items-center space-x-3"><CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" /> <span className="text-slate-700">Discover winnable keywords with Keyword Research.</span></li>
                  <li className="flex items-center space-x-3"><CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" /> <span className="text-slate-700">Audit listings & find quick wins in the Improvement Hub.</span></li>
                </ul>
              </div>

              <div className="print-hide">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 flex-wrap">
                  <button
                    onClick={() => {
                      logEvent(analytics, 'click_signup_trial', { source: 'Hero_SignUp' });
                      openAuthModal('signup');
                    }}
                    className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 px-8 rounded-full text-lg shadow-md hover:shadow-lg hover:scale-[1.02] transition-transform inline-block w-full sm:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
                  >
                    Sign Up For a Free Trial
                  </button>

                  <a
                    href="https://www.teacherspayteachers.com/Product/TPT-Seller-SEO-Listing-Optimizer-App-Create-Titles-Descriptions-SEO-Ranking-13645689?utm_source=My%20site&utm_campaign=Mr%20Joel%20Site%20Landing%20Page"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => logEvent(analytics, 'click_buy_now', { source: 'Hero_BuyNow', value: 25.00, currency: 'USD' })}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-full text-lg shadow-md hover:shadow-lg hover:scale-[1.02] transition-transform inline-block w-full sm:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500/40"
                  >
                    Buy Full Access Now
                  </a>

                  <a
                    href="/app"
                    onClick={() => logEvent(analytics, 'click_launch_app', { source: 'Hero_LaunchApp' })}
                    className="bg-white hover:bg-slate-100 text-sky-600 font-bold py-3 px-8 rounded-full text-lg shadow-sm hover:shadow inline-block w-full sm:w-auto border border-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
                  >
                    Launch App
                  </a>
                </div>
                <p className="mt-4 text-sm text-slate-500">No credit card required for trial.</p>
              </div>

              <div className="w-full relative">
                <div className="relative w-full max-w-5xl mx-auto aspect-[1851/931] bg-white p-2 sm:p-4 rounded-2xl shadow-2xl border border-slate-200 overflow-hidden carousel-frame ring-1 ring-slate-100">
                  {slides.map((slide, index) => (
                    <div key={index} className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}>
                      <img src={slide} alt={`Feature ${index + 1}`} className="w-full h-full object-cover rounded-xl" loading="lazy"/>
                    </div>
                  ))}
                </div>

                {/* Controls hidden on print */}
                <button
                  onClick={() => handleCarouselNavigation('prev')}
                  className="carousel-controls absolute top-1/2 left-2 -translate-y-1/2 sm:left-0 sm:-translate-x-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md backdrop-blur-sm transition-all opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
                >
                  <ChevronLeft className="h-6 w-6 text-slate-700" />
                </button>
                <button
                  onClick={() => handleCarouselNavigation('next')}
                  className="carousel-controls absolute top-1/2 right-2 -translate-y-1/2 sm:right-0 sm:translate-x-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-md backdrop-blur-sm transition-all opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
                >
                  <ChevronRight className="h-6 w-6 text-slate-700" />
                </button>
                <div className="carousel-dots absolute -bottom-8 left-1/2 -translate-x-1/2 flex space-x-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => { clearInterval(carouselIntervalRef.current); setCurrentSlide(index); carouselIntervalRef.current = setInterval(() => setCurrentSlide(prev => (prev + 1) % slides.length), 5000); }}
                      className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${index === currentSlide ? 'bg-sky-600' : 'bg-slate-300'} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50`}
                    />
                  ))}
                </div>
              </div>

            </div>
          </div>

          <section className="py-16 sm:py-20">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-slate-200/70 pt-12">
              <h2 className="text-center text-2xl font-semibold text-slate-700 mb-2">Trusted by Hundreds of TPT Sellers Like You</h2>
              <div className="mt-12 overflow-hidden">
                <div className="testimonial-grid opacity-100 transition-opacity duration-700 ease-in-out">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {testimonials.slice(testimonialGroup, testimonialGroup + 3).map((testimonial, index) => (
                      <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow border border-slate-200 transition-shadow flex flex-col justify-center h-full">
                        <div>
                          <div className="flex items-center gap-1 mb-3 text-amber-400">
                            {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                          </div>
                          <p className="text-slate-600">"{testimonial.quote}"</p>
                          <p className="mt-4 font-bold text-slate-800">- {testimonial.author}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FEATURES GRID */}
          <section id="features" className="py-16 sm:py-20 bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-slate-200/70 pt-12">
              <div className="text-center">
                <h2 className="text-3xl font-extrabold text-sky-800 tracking-tight mb-2">Your All-in-One TPT Toolkit</h2>
                <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">
                  Everything you need to optimize your listings, track your performance, and market your store.
                </p>
              </div>

              <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow transition-shadow flex flex-col items-center text-center">
                  <FilePenLine className="h-8 w-8 text-sky-500 mx-auto" />
                  <h3 className="mt-4 text-lg font-bold">SEO Description Generator</h3>
                  <p className="mt-2 text-sm text-slate-600">Generate comprehensive, keyword-rich SEO descriptions from scratch, formatted in HTML and ready for TPT.</p>
                </div>

                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow transition-shadow flex flex-col items-center text-center">
                  <Lightbulb className="h-8 w-8 text-sky-500 mx-auto" />
                  <h3 className="mt-4 text-lg font-bold">Product Title Generator</h3>
                  <p className="mt-2 text-sm text-slate-600">Get multiple creative and SEO-friendly title options that analyze top-performing listings to increase clicks.</p>
                </div>

                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow transition-shadow flex flex-col items-center text-center">
                  <Sparkles className="h-8 w-8 text-sky-500 mx-auto" />
                  <h3 className="mt-4 text-lg font-bold">Improve & Rewrite Listings</h3>
                  <p className="mt-2 text-sm text-slate-600">Revitalize old listings by importing via TPT URL and letting our smart tools rewrite and restructure them for maximum impact.</p>
                </div>

                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow transition-shadow flex flex-col items-center text-center">
                  <BarChart3 className="h-8 w-8 text-green-500 mx-auto" />
                  <h3 className="mt-4 text-lg font-bold">Live SEO Rank Tracking</h3>
                  <p className="mt-2 text-sm text-slate-600">See exactly where your products rank on TPT for any keyword. Track your position and analyze competitors.</p>
                </div>

                {/* Keyword Research */}
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow transition-shadow flex flex-col items-center text-center">
                  <Search className="h-8 w-8 text-sky-500 mx-auto" />
                  <h3 className="mt-4 text-lg font-bold">Keyword Research</h3>
                  <p className="mt-2 text-sm text-slate-600">
                    Analyze competition, first-page pricing, and popularity for any phrase. Get related keyword opportunities and auto-save to your research hub.
                  </p>
                </div>

                {/* Social Media */}
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow transition-shadow flex flex-col items-center text-center">
                  <Share2 className="h-8 w-8 text-indigo-500 mx-auto" />
                  <h3 className="mt-4 text-lg font-bold">Social Media Generator</h3>
                  <p className="mt-2 text-sm text-slate-600">Turn any product into a week's worth of marketing content for Pinterest, Instagram, Facebook, and more.</p>
                </div>

                {/* Resource Management */}
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow transition-shadow flex flex-col items-center text-center">
                  <FolderKanban className="h-8 w-8 text-slate-500 mx-auto" />
                  <h3 className="mt-4 text-lg font-bold">Resource Management</h3>
                  <p className="mt-2 text-sm text-slate-600">Keep your TPT business organized by saving all your product links and generated description "snapshots" for quick access.</p>
                </div>

                {/* Product Improvement Hub */}
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow transition-shadow flex flex-col items-center text-center">
                  <Gauge className="h-8 w-8 text-sky-500 mx-auto" />
                  <h3 className="mt-4 text-lg font-bold">Product Improvement Hub</h3>
                  <p className="mt-2 text-sm text-slate-600">
                    Audit SEO & quality, surface quick wins, filter by issues/segments, refresh structured tags, and view rank history per tracked keyword.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-sky-50 py-16">
            <div className="max-w-4xl mx-auto text-center px-4 border-t border-slate-200/70 pt-12">
              <h2 className="text-3xl font-extrabold text-sky-800 mb-2">Ready to Optimize Your Listings?</h2>
              <p className="mt-4 text-lg text-slate-600">
                Take the guesswork out of TPT success. Start for free and see the difference.
              </p>
              <button
                onClick={() => {
                  logEvent(analytics, 'click_signup_trial', { source: 'FeaturesCTA_FreeListing' });
                  openAuthModal('signup');
                }}
                className="mt-6 bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 px-8 rounded-full text-lg shadow-md hover:shadow-lg hover:scale-[1.02] transition-transform inline-block print-hide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
              >
                Create Your First Listing for Free
              </button>
              <p className="mt-3 text-sm text-slate-500 print-hide">No credit card required.</p>
            </div>
          </section>

          {/* ====================== HOW-TO (CONSISTENT) ====================== */}
          <div id="how-to-use" className="space-y-12 py-20 sm:py-24">
            <section>
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center border-t border-slate-200/70 pt-12">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-sky-800 tracking-tight mb-2">Go From Idea to Bestselling Listing in Minutes</h2>
                <p className="mt-4 max-w-3xl mx-auto text-lg text-slate-600">
                  Eliminate writer’s block and build your product pages with tools designed for high performance on TPT.
                </p>
              </div>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 space-y-12">
                {/* Importing products */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img src="https://i.imgur.com/i46YR3e.png" alt="Importing products" className="rounded-xl w-full h-auto" loading="lazy"/>
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Importing products</p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Import Any Product, or Your Entire Store</h3>
                    <p className="mt-2 text-slate-600">A massive time-saver for every TPT seller. Paste a single TPT URL to instantly grab a product’s details, or enter your main store URL to bulk import all your listings at once.</p>
                    <p className="mt-2 text-slate-600">This populates your personal in-app library, giving you a central dashboard to manage and optimize all your listings.</p>
                  </div>
                </div>
                {/* Rewriting an existing description */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="md:order-last bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img src="https://i.imgur.com/YnC5Spq.png" alt="Rewriting an existing description" className="rounded-xl w-full h-auto" loading="lazy"/>
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Rewriting an existing description</p>
                  </div>
                  <div className="md:order-first">
                    <h3 className="text-2xl font-bold text-slate-900">Rewrite and Revitalize Existing Listings</h3>
                    <p className="mt-2 text-slate-600">Breathe new life into underperforming products. Our rewrite tool can enhance introductions, add “How to Use” sections, align to curriculum standards, and apply seasonal themes.</p>
                    <p className="mt-2 text-slate-600">Make your time-tested resources feel brand new again and capture a wider audience.</p>
                  </div>
                </div>
                {/* Generating titles */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img src="https://i.imgur.com/TLrAECj.png" alt="Generating titles" className="rounded-xl w-full h-auto" loading="lazy"/>
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Generating titles</p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Generate Titles That Get Clicks</h3>
                    <p className="mt-2 text-slate-600">Get multiple creative and SEO-friendly title options tailored to your resource. Our generator analyzes top-performing listings to suggest titles that increase clicks and are designed to rank higher in TPT search.</p>
                    <p className="mt-2 text-slate-600">Your title is the first thing a buyer sees; make it count.</p>
                  </div>
                </div>
                {/* New description form */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="md:order-last bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img src="https://i.imgur.com/xJVUzfS.png" alt="New description form" className="rounded-xl w-full h-auto" loading="lazy"/>
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">New description form</p>
                  </div>
                  <div className="md:order-first">
                    <h3 className="text-2xl font-bold text-slate-900">Create New SEO Descriptions From Scratch</h3>
                    <p className="mt-2 text-slate-600">Start with a powerful title and use our guided form to generate a complete, professionally formatted HTML description. Define your target audience, tone, and key features to produce content that converts.</p>
                    <p className="mt-2 text-slate-600">Our guided process ensures no optimization is missed.</p>
                  </div>
                </div>
                {/* Adding standards and backlinks */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img src="https://i.imgur.com/pCxgEfe.png" alt="Adding standards and backlinks" className="rounded-xl w-full h-auto" loading="lazy"/>
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Adding standards and backlinks</p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Integrate Standards &amp; Backlinks Seamlessly</h3>
                    <p className="mt-2 text-slate-600">Add value and professionalism to your listings by easily selecting and embedding relevant curriculum standards. Plus, automatically insert links to your other products to drive more sales across your store.</p>
                    <p className="mt-2 text-slate-600">Increase your average cart value effortlessly.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* ====================== SEO SECTION (CONSISTENT) ====================== */}
            <section className="bg-gradient-to-br from-sky-50 to-blue-50 py-20 sm:py-24">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center border-t border-slate-200/70 pt-12">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-green-800 tracking-tight">Climb to Page One with Live SEO Data</h2>
                <p className="mt-4 max-w-3xl mx-auto text-lg text-slate-600">
                  Make data-driven decisions to outrank your competition. Our SEO dashboard pulls back the curtain on the TPT algorithm, giving you a clear path to the top.
                </p>
              </div>

              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 space-y-12">
                {/* Live rank tracking */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="md:order-last bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img src="https://i.imgur.com/oAsOMAG.png" alt="Live keyword rank tracking table" className="rounded-xl w-full h-auto" loading="lazy"/>
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Live keyword rank tracking table</p>
                  </div>
                  <div className="md:order-first">
                    <h3 className="text-2xl font-bold text-slate-900">Live TPT Keyword Rank Tracking</h3>
                    <p className="mt-2 text-slate-600">Stop the guesswork. Our dashboard provides a live, accurate reflection of where your products rank on TPT for any keyword you choose. Monitor your most valuable search terms, track your upward momentum, and see how your optimization efforts translate into real visibility gains. This isn’t just data; it’s the heartbeat of your store’s SEO performance.</p>
                    <div className="text-center my-8">
                      <h4 className="text-xl font-bold text-sky-800">See It in Action</h4>
                      <p className="mt-2 max-w-2xl mx-auto text-slate-600">
                        Stop guessing your product’s rank. Start tracking it live and making data-driven decisions today.
                      </p>
                      <button
                        onClick={() => openAuthModal('login')}
                        className="mt-4 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-full text-lg shadow-md hover:shadow-lg hover:scale-[1.02] transition-transform inline-block print-hide"
                      >
                        Track Your SEO Rank for Free
                      </button>
                    </div>
                  </div>
                </div>

                {/* Competitor research */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img src="https://i.imgur.com/2vONjQi.png" alt="Competitor research dashboard" className="rounded-xl w-full h-auto" loading="lazy"/>
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Competitor research dashboard</p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">In-Depth Competitor Research</h3>
                    <p className="mt-2 text-slate-600">Knowledge is power. For any given keyword, instantly deconstruct the strategies of the top-ranking products. Analyze their titles, pricing, and ratings to understand what makes them successful. Identify market gaps, adopt winning tactics, and position your products to outperform the competition with actionable intelligence.</p>
                  </div>
                </div>

                {/* Keyword discovery */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="md:order-first bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img src="https://i.imgur.com/qVQfPDq.png" alt="Keyword discovery tool" className="rounded-xl w-full h-auto" loading="lazy"/>
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Keyword discovery tool</p>
                  </div>
                  <div className="md:order-last">
                    <h3 className="text-2xl font-bold text-slate-900">Discover High-Value Keywords</h3>
                    <p className="mt-2 text-slate-600">Go beyond the obvious search terms. Our tool analyzes top competitor listings to unearth the high-value, long-tail keywords they are actually using to attract buyers. Get a comprehensive list of these phrases to strategically weave into your titles and descriptions, tapping into new streams of organic traffic.</p>
                  </div>
                </div>

                {/* Phrase analysis */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="md:order-last bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img src="https://i.imgur.com/IMNSVau.png" alt="Phrase analysis tool" className="rounded-xl w-full h-auto" loading="lazy"/>
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Phrase analysis tool</p>
                  </div>
                  <div className="md:order-first">
                    <h3 className="text-2xl font-bold text-slate-900">Analyze Winning Title &amp; Description Phrases</h3>
                    <p className="mt-2 text-slate-600">Why reinvent the wheel? We scan the top 30 listings for any search term and extract the most common and powerful phrases they use in their titles and opening lines. See what language resonates with buyers and the TPT algorithm, and use these proven building blocks to craft compelling copy that gets clicks and converts.</p>
                  </div>
                </div>

                {/* SEO Action Center */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img src="https://i.imgur.com/90JqicU.png" alt="SEO Action Center with suggestions" className="rounded-xl w-full h-auto" loading="lazy"/>
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">SEO Action Center with suggestions</p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Get Your Actionable SEO Roadmap</h3>
                    <p className="mt-2 text-slate-600">Data is useless without action. Our SEO Action Center doesn’t just show you numbers; it gives you a personalized, step-by-step roadmap for improvement. Receive specific, AI-powered suggestions for new titles, impactful opening lines, and strategic keywords to add, turning complex analysis into a simple, actionable checklist for climbing to page one.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* ====================== KEYWORD RESEARCH (BULKED + BIGGER HEADINGS) ====================== */}
            <section id="keyword-research" className="py-20 sm:py-24 bg-white">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-slate-200/70 pt-12 text-center">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-sky-800 tracking-tight">Keyword Research</h2>
                <p className="mt-4 max-w-3xl mx-auto text-lg text-slate-600">
                  Analyze any TPT keyword in seconds, uncover related opportunities you can actually rank for, and build a living research library—all in one workflow. Get competition, first-page pricing, real-time popularity signals, and a curated list of “winnable” phrases you can act on today.
                </p>
              </div>

              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 space-y-16">
                {/* Competition • Popularity • Top 5 */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img
                      src="https://i.imgur.com/yM3cNiD.png"
                      alt="Competition • Popularity • Top 5"
                      className="rounded-xl w-full h-auto block"
                      loading="lazy"
                    />
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Competition • Popularity • Top 5</p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">See Competition &amp; Popularity at a Glance</h3>
                    <p className="mt-4 text-slate-600">
                      Type any phrase and instantly see <strong>total competition</strong>, <strong>first-page average price</strong>, and an <strong>Algolia-style popularity signal</strong> for the exact term. We also surface the <strong>Top 5 ranking products</strong> so you know exactly who you’re up against and why they’re winning.
                    </p>
                    <ul className="mt-5 space-y-2 text-[15.5px] text-slate-700">
                      <li>• Quick scan of difficulty vs. demand</li>
                      <li>• Real products, not abstract metrics</li>
                      <li>• Instant context before you commit effort</li>
                    </ul>
                  </div>
                </div>

                {/* Related Opportunities Grid */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="md:order-last bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img
                      src="https://i.imgur.com/YXkih49.png"
                      alt="Related Opportunities Grid"
                      className="rounded-xl w-full h-auto block"
                      loading="lazy"
                    />
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Related Opportunities Grid</p>
                  </div>
                  <div className="md:order-first">
                    <h3 className="text-2xl font-bold text-slate-900">Find Winnable Keyword Opportunities</h3>
                    <p className="mt-4 text-slate-600">
                      Get related ideas labeled <strong>Gold Mine</strong>, <strong>Excellent</strong>, <strong>Good</strong>, or <strong>Niche</strong> so you can prioritize high-impact phrases fast. Results are globally cached for <strong>7 days</strong> to make repeat checks snappy.
                    </p>
                    <ul className="mt-5 space-y-2 text-[15.5px] text-slate-700">
                      <li>• Expand beyond obvious terms with data-backed suggestions</li>
                      <li>• Balance demand vs. feasibility before you write</li>
                      <li>• Build a pipeline of future product ideas</li>
                    </ul>
                  </div>
                </div>

                {/* Research Hub • CSV Export */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img
                      src="https://i.imgur.com/51cQEpV.png"
                      alt="Research Hub • CSV Export"
                      className="rounded-xl w-full h-auto block"
                      loading="lazy"
                    />
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Research Hub • CSV Export</p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Build a Personal Research Hub</h3>
                    <p className="mt-4 text-slate-600">
                      Every search is <strong>auto-saved</strong> with sorting, niche tagging, and <strong>one-click CSV export</strong>. Perfect for planning launches, collaborating, and reporting progress over time.
                    </p>
                    <ul className="mt-5 space-y-2 text-[15.5px] text-slate-700">
                      <li>• Keep a clean history of what you’ve tested</li>
                      <li>• Tag by season, grade, subject, or intent</li>
                      <li>• Export to share with partners or VA’s</li>
                    </ul>
                    <a
                      href="https://mrjoelstptgenerator.com/app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 px-6 rounded-full shadow-sm hover:shadow"
                    >
                      Try the Keyword Tool
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* ====================== PRODUCT IMPROVEMENT HUB (BULKED + BIGGER HEADINGS) ====================== */}
            <section id="product-improvement-hub" className="py-20 sm:py-24">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-slate-200/70 pt-12 text-center">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-sky-800 tracking-tight">Product Improvement Hub</h2>
                <p className="mt-4 max-w-3xl mx-auto text-lg text-slate-600">
                  Audit your entire catalog at a glance, surface the highest-leverage fixes, and refresh product data in one place. Turn scattered to-dos into a focused upgrade plan that moves rankings and sales.
                </p>
              </div>

              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 space-y-16">
                {/* SEO & Quality Score Panel */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img
                      src="https://i.imgur.com/894M0ha.png"
                      alt="SEO & Quality Score Panel"
                      className="rounded-xl w-full h-auto block"
                      loading="lazy"
                    />
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">SEO &amp; Quality Score Panel</p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Fix-First SEO &amp; Quality Checks</h3>
                    <p className="mt-4 text-slate-600">
                      Instantly see <strong>title length/keywords</strong>, <strong>description depth</strong>,
                      <strong> bullets</strong>, <strong>readability</strong>, and <strong>CTA strength</strong>—ranked by impact so you
                      know what to fix first. Intelligence flags <strong>Quick Wins</strong>, <strong>Trending Up/Down</strong>,
                      <strong> Seasonal cues</strong>, and <strong>High-Potential</strong> listings.
                    </p>
                    <ul className="mt-5 space-y-2 text-[15.5px] text-slate-700">
                      <li>• Convert messy audits into a clear, prioritized list</li>
                      <li>• See why a page isn’t performing and what to change</li>
                      <li>• Tackle high-ROI fixes before deep rewrites</li>
                    </ul>
                  </div>
                </div>

                {/* Filters & Segments */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="md:order-last bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img
                      src="https://i.imgur.com/bGPfk9g.png"
                      alt="Filters & Segments"
                      className="rounded-xl w-full h-auto block"
                      loading="lazy"
                    />
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Filters &amp; Segments</p>
                  </div>
                  <div className="md:order-first">
                    <h3 className="text-2xl font-bold text-slate-900">Powerful Filters to Focus Your Time</h3>
                    <p className="mt-4 text-slate-600">
                      Slice your catalog by <strong>health</strong>, <strong>issue type</strong>, <strong>activity</strong>,
                      <strong> grade</strong>, <strong>subject area/specific subject</strong>, <strong>seasonal theme</strong>,
                      <strong> resource type</strong>, and <strong>custom tags</strong> to zero in on the work that actually moves metrics.
                    </p>
                    <ul className="mt-5 space-y-2 text-[15.5px] text-slate-700">
                      <li>• Build quick fix-lists by audience, season, or topic</li>
                      <li>• Park “later” items without losing track</li>
                      <li>• Work top-down across hundreds of listings</li>
                    </ul>
                  </div>
                </div>

                {/* One-Click Maintenance & Rank History */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img
                      src="https://i.imgur.com/AVPwcBW.png"
                      alt="One-Click Maintenance & Rank History"
                      className="rounded-xl w-full h-auto block"
                      loading="lazy"
                    />
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">One-Click Maintenance &amp; Rank History</p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Refresh Data with One Click</h3>
                    <p className="mt-4 text-slate-600">
                      Update <strong>All Audits</strong>, refresh structured product tags from TPT, or update a
                      single product while iterating. Open a rank-history graph for any tracked keyword to visualize
                      movement over time and tie improvements to results.
                    </p>
                    <ul className="mt-5 space-y-2 text-[15.5px] text-slate-700">
                      <li>• Keep audits fresh as you ship changes</li>
                      <li>• See cause-and-effect between edits and rank</li>
                      <li>• Work in focused loops: analyze → fix → re-check</li>
                    </ul>
                    <a
                      href="https://mrjoelstptgenerator.com/app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-6 inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 px-6 rounded-full shadow-sm hover:shadow"
                    >
                      Open the Hub
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* ====================== SOCIAL (CONSISTENT) ====================== */}
            <section className="py-20 sm:py-24">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center border-t border-slate-200/70 pt-12">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-indigo-800 tracking-tight">Automate Your Social Media Marketing</h2>
                <p className="mt-4 max-w-3xl mx-auto text-lg text-slate-600">
                  Save hours every week on promotion. Select any of your TPT products and instantly generate a library of engaging social media content with a single click.
                </p>
              </div>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 space-y-12">
                {/* Instant Post Generation */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img src="https://i.imgur.com/thc4B9u.png" alt="Options for generating social media posts" className="rounded-xl w-full h-auto" loading="lazy"/>
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Options for generating social media posts</p>
                  </div>
                  <div className="md:order-first">
                    <h3 className="text-2xl font-bold text-slate-900">Instant Post Generation</h3>
                    <p className="mt-2 text-slate-600">Select a product, choose your goal (like a new product announcement or a sale), set the tone, and our app does the rest, crafting multiple unique posts for you.</p>
                    <p className="mt-2 text-slate-600">Stay top-of-mind with your audience without the extra work.</p>
                  </div>
                </div>
                {/* Platform-Specific Content */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                  <div className="md:order-last bg-white p-2 rounded-2xl shadow-2xl border overflow-hidden">
                    <img src="https://i.imgur.com/Zi6LhEQ.png" alt="Generated posts for different social media platforms" className="rounded-xl w-full h-auto" loading="lazy"/>
                    <p className="text-sm uppercase tracking-wide text-slate-500 p-3">Generated posts for different social media platforms</p>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Platform-Specific Content That Works</h3>
                    <p className="mt-2 text-slate-600">Get content perfectly tailored for Pinterest, Instagram, Facebook, and X (Twitter). Posts are formatted with best practices, including hashtags, calls to action, and platform-specific language.</p>
                    <p className="mt-2 text-slate-600">Each post is crafted by a marketing expert to maximize engagement.</p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <section className="py-20 sm:py-24">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-slate-200/70 pt-12">
              <div className="text-center">
                <h2 className="text-3xl font-extrabold text-sky-800 tracking-tight mb-2">
                  Built for Every TPT Seller, at Every Stage
                </h2>
              </div>
              <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow border border-slate-200 transition-shadow">
                  <Rocket className="h-12 w-12 text-sky-500 mx-auto" />
                  <h3 className="mt-4 text-xl font-bold">The New TPT Seller</h3>
                  <p className="mt-2 text-slate-600">Start strong with professionally formatted, SEO-optimized listings that make a great first impression.</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow border border-slate-200 transition-shadow">
                  <Gem className="h-12 w-12 text-sky-500 mx-auto" />
                  <h3 className="mt-4 text-xl font-bold">The Veteran TPT Seller</h3>
                  <p className="mt-2 text-slate-600">Save countless hours by bulk-importing your store and revitalizing your entire catalog with powerful rewrite tools.</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow border border-slate-200 transition-shadow">
                  <PenTool className="h-12 w-12 text-sky-500 mx-auto" />
                  <h3 className="mt-4 text-xl font-bold">The Teacher-Author</h3>
                  <p className="mt-2 text-slate-600">Focus on what you do best—creating amazing resources. Let our app handle the descriptions, SEO, and marketing.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-br from-sky-50 to-blue-50">
            <div className="max-w-4xl mx-auto text-center py-16 sm:py-20 px-4 border-t border-slate-200/70 pt-12">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-sky-800 mb-2">
                Stop Guessing, Start Growing
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Join hundreds of successful TPT sellers. Your first optimized listing is just a few clicks away.
              </p>
              <button
                onClick={() => {
                  logEvent(analytics, 'click_signup_trial', { source: 'FinalCTA_SignUp' });
                  openAuthModal('signup');
                }}
                className="mt-8 bg-sky-600 hover:bg-sky-700 text-white font-bold py-4 px-10 rounded-full text-xl shadow-md hover:shadow-lg hover:scale-[1.02] transition-transform inline-block print-hide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50"
              >
                Start Your Free Trial Now
              </button>
              <p className="mt-4 text-sm text-slate-500 print-hide">Free forever plan. No credit card required.</p>
            </div>
          </section>

          <section id="pricing" className="py-20 sm:py-24 bg-white">
            <div className="max-w-2xl mx-auto text-center px-4 border-t border-slate-200/70 pt-12">
              <h2 className="text-3xl font-extrabold text-sky-800 mb-2">One Simple Price. Lifetime Access.</h2>
              <div className="mt-8 bg-slate-50 p-8 rounded-2xl shadow-2xl border border-slate-200">
                <p className="text-6xl font-extrabold text-slate-900">$79.99</p>
                <p className="text-lg font-semibold text-slate-600">One-Time Payment</p>
                <ul className="mt-6 text-left space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    Lifetime access to all tools
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    All future updates included
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    Unlimited generations
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    Secure payment via TPT
                  </li>
                </ul>
                <a
                  href="https://www.teacherspayteachers.com/Product/TPT-Seller-SEO-Listing-Optimizer-App-Create-Titles-Descriptions-SEO-Ranking-13645689?utm_source=My%20site&utm_campaign=Mr%20Joel%20Site%20Landing%20Page"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => logEvent(analytics, 'click_buy_now', { source: 'Pricing_BuyNow', value: 25.00, currency: 'USD' })}
                  className="mt-8 bg-sky-600 hover:bg-sky-700 text-white font-bold py-4 px-10 rounded-full text-lg shadow-md hover:shadow-lg hover:scale-[1.02] transition-transform inline-block w-full print-hide"
                >
                  Buy Now & Unlock All Features
                </a>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="py-20 sm:py-24">
            <div className="max-w-3xl mx-auto px-4 border-t border-slate-200/70 pt-12">
              <h2 className="text-center text-3xl font-extrabold text-sky-800 mb-2">Frequently Asked Questions</h2>
              <div className="mt-10 space-y-4">

                <details className="bg-white p-6 rounded-xl shadow-sm group border border-slate-200">
                  <summary className="flex justify-between items-center font-semibold cursor-pointer text-slate-800">
                    Is this a one-time purchase?
                    <ChevronDown className="h-5 w-5 transition-transform transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-4 text-slate-600">
                    Yes! You pay once and get lifetime access to the app and all future updates. No subscriptions, no hidden fees.
                  </p>
                </details>

                <details className="bg-white p-6 rounded-xl shadow-sm group border border-slate-200">
                  <summary className="flex justify-between items-center font-semibold cursor-pointer text-slate-800">
                    Do I need to give you my TPT password?
                    <ChevronDown className="h-5 w-5 transition-transform transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-4 text-slate-600">
                    Absolutely not. The app only uses public product or store URLs to import information. You never enter your TPT login details into our tool.
                  </p>
                </details>

                <details className="bg-white p-6 rounded-xl shadow-sm group border border-slate-200">
                  <summary className="flex justify-between items-center font-semibold cursor-pointer text-slate-800">
                    How do I get access after purchase?
                    <ChevronDown className="h-5 w-5 transition-transform transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-4 text-slate-600">
                    Purchase on TPT, then email your purchase confirmation to us. We manually approve your account (typically within 6–12 hours). Instructions are included in the PDF you receive on TPT.
                  </p>
                </details>

                <details className="bg-white p-6 rounded-xl shadow-sm group border border-slate-200">
                  <summary className="flex justify-between items-center font-semibold cursor-pointer text-slate-800">
                    Can I import my entire store or just single products?
                    <ChevronDown className="h-5 w-5 transition-transform transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-4 text-slate-600">
                    Both. Paste a single product URL for a one-off import, or your store URL to bulk-import your listings into your in-app library.
                  </p>
                </details>

                <details className="bg-white p-6 rounded-xl shadow-sm group border border-slate-200">
                  <summary className="flex justify-between items-center font-semibold cursor-pointer text-slate-800">
                    How fresh is the keyword data and rank tracking?
                    <ChevronDown className="h-5 w-5 transition-transform transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-4 text-slate-600">
                    Rank tracking is live when you query it. Keyword research results are globally cached for 7 days to keep repeat checks fast while reducing load. You can re-check anytime.
                  </p>
                </details>

                <details className="bg-white p-6 rounded-xl shadow-sm group border border-slate-200">
                  <summary className="flex justify-between items-center font-semibold cursor-pointer text-slate-800">
                    Can I export my research or generated content?
                    <ChevronDown className="h-5 w-5 transition-transform transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-4 text-slate-600">
                    Yes. Your keyword research history supports one-click CSV export, and description/title generations can be saved as snapshots in your library.
                  </p>
                </details>

                <details className="bg-white p-6 rounded-xl shadow-sm group border border-slate-200">
                  <summary className="flex justify-between items-center font-semibold cursor-pointer text-slate-800">
                    Does it help with curriculum standards and backlinks?
                    <ChevronDown className="h-5 w-5 transition-transform transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-4 text-slate-600">
                    Yes. You can select relevant curriculum standards and automatically add backlinks to your other products to lift internal discoverability and cart value.
                  </p>
                </details>

                <details className="bg-white p-6 rounded-xl shadow-sm group border border-slate-200">
                  <summary className="flex justify-between items-center font-semibold cursor-pointer text-slate-800">
                    Which platforms does the social media tool support?
                    <ChevronDown className="h-5 w-5 transition-transform transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-4 text-slate-600">
                    Pinterest, Instagram, Facebook, and X (Twitter), with copy tuned to platform best practices.
                  </p>
                </details>

                <details className="bg-white p-6 rounded-xl shadow-sm group border border-slate-200">
                  <summary className="flex justify-between items-center font-semibold cursor-pointer text-slate-800">
                    Will it work on Mac and Windows? Do I need to install anything?
                    <ChevronDown className="h-5 w-5 transition-transform transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-4 text-slate-600">
                    It’s a web app—no installation needed. Use any modern browser on Mac or Windows (Chrome recommended).
                  </p>
                </details>

                <details className="bg-white p-6 rounded-xl shadow-sm group border border-slate-200">
                  <summary className="flex justify-between items-center font-semibold cursor-pointer text-slate-800">
                    Is there a free trial?
                    <ChevronDown className="h-5 w-5 transition-transform transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-4 text-slate-600">
                    Yes—sign up and explore core features without a credit card. You can upgrade anytime to unlock everything.
                  </p>
                </details>

              </div>
            </div>
          </section>
        </main>

        <footer className="bg-slate-800 text-slate-400 py-12">
          <div className="max-w-5xl mx-auto text-center px-4">
            <p>&copy; 2025 Mr. Joel's Seller Tools. All Rights Reserved.</p>
            <div className="mt-4 space-x-4">
              <a href="#" className="hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50 rounded">Contact</a>
              <Link to="/privacy-policy" className="hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50 rounded">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/50 rounded">Terms of Service</Link>
            </div>
          </div>
        </footer>
      </div>

      {showScrollCTA && (
        <ScrollCTA
          onClose={() => setShowScrollCTA(false)}
          onOpenAuthModal={() => openAuthModal('signup')}
        />
      )}

      {/* ADDED: The Landing Page Walkthrough Tour */}
      <LandingWalkthrough />

      {isAuthModalOpen && (
        <AuthModal
          onClose={() => setIsAuthModalOpen(false)}
          authMode={authMode}
          setAuthMode={setAuthMode}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          authError={authError}
          setAuthError={setAuthError}
          showForgotPassword={showForgotPassword}
          setShowForgotPassword={setShowForgotPassword}
          resetEmail={resetEmail}
          setResetEmail={setResetEmail}
          resetMessage={resetMessage}
          setResetMessage={setResetMessage}
          tptStoreUrlInput={tptStoreUrlInput}
          setTptStoreUrlInput={setTptStoreUrlInput}
          handleAuthAction={handleAuthAction}
          handlePasswordReset={handlePasswordReset}
          handleGoogleSignIn={handleGoogleSignIn}
        />
      )}
    </>
  );
};

export default LandingPage;